// @flow
import * as React from 'react';
import Modal from 'react-modal';

import {
  ComposableTable,
  getTextCellGenerator,
  getButtonCellGenerator,
  getErrorCellGenerator,
  getErrorPercentageCellGenerator,
  getTimestampCellGenerator,
} from 'components/ComposableTable';
import DeliveryModalContainer from 'chat/components/DeliveryModalContainer';
import Heading from 'components/Heading';
import Link from 'components/Link';
import Row from 'components/Row';
import Text from 'components/Text';

import type {
  GetAutoMessageByUuid_autoMessaging_autoMessages_autoMessages_previousSends as AutoMessagePreviousSends,
  GetAutoMessageByUuid_autoMessaging_autoMessages_autoMessages_previousSends_edges_node_readSummary as AutoMessageSendReadSummary,
} from 'admin/components/AdminAutoMessages/queries/__generated__/GetAutoMessageByUuid';

type AutoMessageSendReadSummaryInfo = {
  readSummary: AutoMessageSendReadSummary,
  streamUuid: string,
  messageUuid: string,
};

type Props = {
  previousSends: ?AutoMessagePreviousSends,
  fileUploadsUrlSlug: string,
};

type State = {
  showDeliveryModal: boolean,
  readSummaryInfo: ?AutoMessageSendReadSummaryInfo,
};

export default class AdminAutoMessageDetailPreviousSendsTable extends React.Component<
  Props,
  State
> {
  state = {
    showDeliveryModal: false,
    readSummaryInfo: undefined,
  };

  showDeliveryModal = (readSummaryInfo: AutoMessageSendReadSummaryInfo) => {
    this.setState({ readSummaryInfo }, () => this.setState({ showDeliveryModal: true }));
  };

  hideDeliveryModal = () => {
    this.setState({ readSummaryInfo: undefined, showDeliveryModal: false });
  };

  formatDataToRow = (previousSend: *) => {
    const { uuid, sentAt, readSummary, streamUuid, messageUuid } = previousSend.node;
    if (!readSummary) {
      return {
        uuid,
        sentAt,
      };
    }

    const { recipientCount, identificationErrorEntry, failedEntry, unreachableEntry } = readSummary;
    const deliveryErrorPercentage = failedEntry ? failedEntry.count / recipientCount : 0;
    const unreachableCount = unreachableEntry ? unreachableEntry.count : 0;
    const identificationErrorCount = identificationErrorEntry ? identificationErrorEntry.count : 0;
    const recipientErrorCount = unreachableCount + identificationErrorCount;

    return {
      uuid,
      sentAt,
      recipientCount,
      recipientErrorCount,
      deliveryErrorPercentage,
      readSummaryInfo: {
        readSummary,
        streamUuid,
        messageUuid,
      },
    };
  };

  render() {
    const { previousSends, fileUploadsUrlSlug } = this.props;
    const columnHeaders = [
      { name: 'Time sent' },
      { name: 'Recipients' },
      { name: 'Recipient errors' },
      { name: 'Delivery errors' },
      { name: '' },
    ];

    const columnGenerators = [
      getTimestampCellGenerator('sentAt'),
      getTextCellGenerator('recipientCount'),
      getErrorCellGenerator('recipientErrorCount'),
      getErrorPercentageCellGenerator('deliveryErrorPercentage'),
      getButtonCellGenerator({
        fieldForAction: 'readSummaryInfo',
        onClickAction: data => this.showDeliveryModal(data),
        buttonLabel: 'View details',
        buttonProps: { level: 'secondary', size: 'xsmall', 'data-test': 'view-details-button' },
      }),
    ];
    const sends = previousSends && previousSends.edges;
    const rows = sends ? sends.map(previousSend => this.formatDataToRow(previousSend)) : [];
    return (
      <React.Fragment>
        {this.state.readSummaryInfo && (
          <Modal isOpen={this.state.showDeliveryModal}>
            <DeliveryModalContainer
              source={{
                type: 'autoMessageSend',
                readSummary: this.state.readSummaryInfo.readSummary,
                threadUuid: this.state.readSummaryInfo.streamUuid,
                messageUuid: this.state.readSummaryInfo.messageUuid,
              }}
              onRequestHide={this.hideDeliveryModal}
            />
          </Modal>
        )}
        <Heading level="4">Sent messages</Heading>
        <Row size="small">
          <Text size="xsmall" color="graphite">
            If you expected a message to be sent but it isn’t showing up here, check the{' '}
            <Link to={fileUploadsUrlSlug}>File Uploads tab</Link> for errors.
          </Text>
        </Row>
        <Row>
          <ComposableTable
            data-test="previous-sends-table"
            columnHeaders={columnHeaders}
            columnGenerators={columnGenerators}
            rows={rows}
            keyField="uuid"
            actionsColumn
          />
        </Row>
        {rows.length === 0 && (
          <Text align="center" element="div" size="small" weight="semibold">
            After this auto message is activated, sent messages will appear here.
          </Text>
        )}
      </React.Fragment>
    );
  }
}
